import { useState, useEffect } from 'react'
import { FiX, FiCheck, FiAlertCircle } from 'react-icons/fi'
import { useStore } from '@/stores/useStore'

interface NFTFormData {
  name: string
  description: string
  price: string
  category: string
}

export default function NFTModal() {
  const { updateHistoryItem } = useStore()
  const [isOpen, setIsOpen] = useState(false)
  const [model, setModel] = useState<any>(null)
  const [formData, setFormData] = useState<NFTFormData>({
    name: '',
    description: '',
    price: '',
    category: 'watch',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    ;(window as any).openNFTModal = (modelData: any) => {
      setModel(modelData)
      setFormData({
        name: modelData.name || '',
        description: modelData.description || '',
        price: '',
        category: 'watch',
      })
      setError(null)
      setSuccess(false)
      setIsOpen(true)
    }

    return () => {
      delete (window as any).openNFTModal
    }
  }, [])

  const handleClose = () => {
    setIsOpen(false)
    setModel(null)
    setError(null)
    setSuccess(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!model) return

    const token = localStorage.getItem('auth_token')
    const walletAddress = localStorage.getItem('wallet_address')

    if (!token) {
      setError('请先登录')
      return
    }

    if (!walletAddress) {
      setError('需要连接 Solana 钱包才能同步到 NFT 平台')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`/api/nft/sync/${model.task_id || model.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          price: parseFloat(formData.price) || 0,
          category: formData.category,
          modelUrl: model.model_url,
          thumbnailUrl: model.thumbnail_url,
          walletAddress,
        }),
      })

      const data = await response.json()

      if (data.success) {
        if (model.id) {
          updateHistoryItem(model.id, {
            nftId: data.data?.nftId,
            syncedToNft: true,
          })
        }
        setSuccess(true)
        setTimeout(() => {
          handleClose()
          window.location.reload()
        }, 1500)
      } else {
        setError(data.error || '上架失败，请重试')
      }
    } catch (err) {
      console.error('NFT sync error:', err)
      setError('网络错误，请重试')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FiX className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">上架 NFT</h2>
          <p className="text-gray-600 mt-1">将您的 3D 模型上架到 NFT 市场</p>
        </div>

        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCheck className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">上架成功！</h3>
            <p className="text-gray-600">您的 NFT 已成功上架到市场</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">NFT 名称</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="输入 NFT 名称"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                rows={3}
                placeholder="描述您的 NFT"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">价格 (SOL)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">类别</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="watch">手表</option>
                <option value="jewelry">珠宝</option>
                <option value="other">其他</option>
              </select>
            </div>

            {error && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 text-red-600 rounded-lg">
                <FiAlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-copper-light to-copper-dark text-white font-semibold rounded-lg hover:from-copper-600 hover:to-copper-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>上架中...</span>
                </>
              ) : (
                <span>确认上架</span>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
